package com.advaita.cloud.drive.ui.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import kotlinx.browser.document
import org.w3c.dom.HTMLElement
import androidx.compose.runtime.key
import com.advaita.cloud.drive.shared.AccessGrantDto
import com.advaita.cloud.drive.shared.FileAccessResponse
import com.advaita.cloud.drive.shared.PickerItemDto
import com.advaita.cloud.drive.ui.store.PickerKind
import com.advaita.cloud.drive.ui.store.RemoveAction
import com.advaita.cloud.drive.ui.store.ShareDialogStore
import com.advaita.cloud.drive.ui.store.levelLabel
import com.advaita.cloud.drive.ui.store.levelOptionsFor
import com.advaita.cloud.drive.ui.store.noMatchLine
import com.advaita.cloud.drive.ui.store.pickerBlockedSentence
import com.advaita.cloud.drive.ui.store.pickerKind
import com.advaita.cloud.drive.ui.store.pickerLabel
import com.advaita.cloud.drive.ui.store.pickerMeta
import com.advaita.cloud.drive.ui.store.principalLabel
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.attributes.selected
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Option
import org.jetbrains.compose.web.dom.Select
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

private const val TITLE_ID = "share-dialog-title"
private const val LISTBOX_ID = "share-results"

private fun optionId(index: Int) = "share-option-$index"

internal const val FOCUSABLE =
    "button:not([disabled]), select:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex='-1'])"

/** Wraps Tab from the last focusable element to the first, and Shift-Tab the other way. */
internal fun trapTab(dialog: HTMLElement, backwards: Boolean, stop: () -> Unit) {
    val nodes = dialog.querySelectorAll(FOCUSABLE)
    if (nodes.length == 0) return
    val first = nodes.item(0) as? HTMLElement ?: return
    val last = nodes.item(nodes.length - 1) as? HTMLElement ?: return
    val active = document.activeElement
    if (backwards && (active == first || dialog.contains(active) != true)) { stop(); last.focus() }
    else if (!backwards && (active == last || dialog.contains(active) != true)) { stop(); first.focus() }
}

/**
 * ADV-1330 F1/F2: the share dialog for one file. It uses the `.modal` shell that every other
 * dialog uses. All state and logic live in [ShareDialogStore]; this renders and routes keys.
 *
 * Top to bottom: the title, the picker (or the picked row with its level and Share), the notes,
 * and "Who has access". A capped dialog (S8) shows the one sentence and Done, nothing else.
 *
 * Keyboard: the picker is a listbox. ArrowDown and ArrowUp move from the input through the rows
 * (`aria-activedescendant`), Enter picks; each row is also a button, so Tab reaches it and Enter
 * or Space picks it. Escape closes the dialog from anywhere inside it. A capped control is
 * `aria-disabled` and points at its sentence with `aria-describedby`.
 */
@Composable
fun ShareModal(dialog: ShareDialogStore) {
    val file = dialog.target ?: return
    // Focus goes back to what opened the dialog (the row's Share button) when it closes.
    DisposableEffect(file.id) {
        val opener = document.activeElement as? HTMLElement
        onDispose { opener?.takeIf { document.body?.contains(it) == true }?.focus() }
    }
    Div(attrs = { classes("modal-backdrop") }) {
        Div(attrs = {
            classes("modal", "wide", "share-modal")
            attr("role", "dialog")
            attr("aria-modal", "true")
            attr("aria-labelledby", TITLE_ID)
            onKeyDown { e ->
                when (e.key) {
                    "Escape" -> { e.stopPropagation(); dialog.close() }
                    // A simple focus trap: Tab and Shift-Tab wrap inside the dialog.
                    "Tab" -> (e.currentTarget as? HTMLElement)?.let { trapTab(it, e.shiftKey) { e.preventDefault() } }
                }
            }
        }) {
            H2(attrs = { classes("modal-title"); attr("id", TITLE_ID) }) { Text("Share \"${file.displayName}\"") }
            val access = dialog.access
            val cap = dialog.loadCap
            when {
                cap != null -> CappedNotice(cap, owner = dialog.ownerName)
                dialog.loadError != null -> Div(attrs = { classes("share-error"); attr("role", "alert") }) { Text(dialog.loadError!!) }
                access == null -> Div(attrs = { classes("share-loading") }) { Text("Loading…") }
                else -> {
                    ShareAddSection(dialog)
                    dialog.noticeCap?.let { CappedNotice(it, owner = dialog.ownerName) }
                    dialog.noticeError?.let { Div(attrs = { classes("share-error"); attr("role", "alert") }) { Text(it) } }
                    AccessList(dialog, access)
                }
            }
            Div(attrs = { classes("modal-actions") }) {
                Button(attrs = {
                    classes("btn", "primary")
                    onClick { dialog.close() }
                }) { Text("Done") }
            }
        }
    }
}

@Composable
private fun ShareAddSection(dialog: ShareDialogStore) {
    val picker = dialog.picker
    val picked = picker.picked
    Div(attrs = { classes("share-add") }) {
        if (picked == null) {
            val active = picker.activeIndex
            // The input sits alone in its wrapper. A password manager puts its own element next to
            // an input, and Compose adds and removes this section's children by
            // position. Next to the list, that element made a pick remove it instead of the old
            // Recent list, which stayed on screen without its icons. Example: Lena picks Sam and
            // shares, and "Recent" showed twice.
            Div(attrs = { classes("share-input-wrap") }) {
                Input(InputType.Text, attrs = {
                    classes("modal-input", "share-input")
                    attr("aria-label", "Add people or teams")
                    attr("role", "combobox")
                    attr("aria-controls", LISTBOX_ID)
                    attr("aria-expanded", picker.items.isNotEmpty().toString())
                    attr("aria-autocomplete", "list")
                    if (active >= 0) attr("aria-activedescendant", optionId(active))
                    placeholder("Type a first name, last name or email")
                    value(picker.query)
                    ref { el -> el.focus(); onDispose { } }
                    onInput { picker.onQueryChanged(it.value) }
                    onKeyDown { e ->
                        when (e.key) {
                            "ArrowDown" -> { e.preventDefault(); picker.moveActive(1) }
                            "ArrowUp" -> { e.preventDefault(); picker.moveActive(-1) }
                            "Enter" -> if (active >= 0) { e.preventDefault(); dialog.chooseActive() }
                        }
                    }
                })
            }
            if (picker.showingRecents && picker.items.isNotEmpty()) {
                Div(attrs = { classes("share-list-label") }) { Text("Recent") }
            }
            Div(attrs = {
                classes("share-results")
                attr("id", LISTBOX_ID)
                attr("role", "listbox")
                attr("aria-label", if (picker.showingRecents) "Recent people and teams" else "People and teams")
            }) {
                picker.items.forEachIndexed { i, item ->
                    PickerRow(item, index = i, active = i == active) { dialog.choose(item) }
                }
            }
            if (picker.isSearching) Div(attrs = { classes("share-note") }) { Text("Searching…") }
            val q = picker.query.trim()
            if (picker.items.isEmpty() && q.isNotEmpty() && picker.completedQuery == q && picker.notes.isEmpty()) {
                Div(attrs = { classes("share-list-label") }) { Text(noMatchLine(q)) }
            }
            picker.notes.forEach { note -> Div(attrs = { classes("share-note"); attr("role", "status") }) { Text(note) } }
        } else {
            val capId = "share-pick-cap"
            val capped = dialog.pickCap != null
            Div(attrs = { classes("share-picked") }) {
                KindIcon(pickerKind(picked))
                Span(attrs = { classes("share-name") }) { Text(pickerLabel(picked)) }
                Button(attrs = {
                    classes("btn", "icon")
                    attr("aria-label", "Clear ${pickerLabel(picked)}")
                    attr("data-tip", "Choose someone else")
                    onClick { dialog.clearChoice() }
                }) { Icon(IconName.CLOSE) }
                LevelSelect(
                    label = "Access for ${pickerLabel(picked)}",
                    current = dialog.level,
                    levels = dialog.levels,
                    enabled = !dialog.busy,
                    onChange = { dialog.chooseLevel(it) },
                )
                Button(attrs = {
                    classes("btn", "primary")
                    // A cap keeps the button focusable, with its reason; busy is a plain disable.
                    if (capped) {
                        attr("aria-disabled", "true")
                        attr("aria-describedby", capId)
                    } else if (!dialog.canShare) {
                        attr("disabled", "")
                    }
                    onClick { if (dialog.canShare) dialog.share() }
                }) { Text(if (dialog.busy) "Sharing…" else "Share") }
            }
            dialog.pickCap?.let { CappedNotice(it, owner = dialog.ownerName, person = pickerLabel(picked), id = capId) }
        }
    }
}

@Composable
private fun PickerRow(item: PickerItemDto, index: Int, active: Boolean, onPick: () -> Unit) {
    val blocked = pickerBlockedSentence(item)
    // A button, so Tab reaches it and Enter or Space picks it. A blocked row stays focusable,
    // with aria-disabled, so its sentence is read.
    Button(attrs = {
        classes("share-result")
        if (blocked != null) classes("blocked")
        if (active) classes("active")
        attr("id", optionId(index))
        attr("type", "button")
        attr("role", "option")
        attr("aria-selected", active.toString())
        if (blocked != null) attr("aria-disabled", "true")
        onClick { if (blocked == null) onPick() }
    }) {
        KindIcon(pickerKind(item))
        Div(attrs = { classes("share-who") }) {
            Div(attrs = { classes("share-name") }) { Text(pickerLabel(item)) }
            val meta = blocked ?: pickerMeta(item)
            if (meta != null) Div(attrs = { classes("share-meta") }) { Text(meta) }
        }
    }
}

@Composable
internal fun KindIcon(kind: PickerKind) {
    Span(attrs = {
        classes("share-kind")
        attr("aria-label", if (kind == PickerKind.TEAM) "Team" else "Person")
    }) { Icon(if (kind == PickerKind.TEAM) IconName.TEAM else IconName.PERSON) }
}

/**
 * A level control. [cappedBy] is the id of the sentence that caps it: the control is then
 * `aria-disabled` and describes itself with that sentence, and the store ignores a change.
 * [enabled] false without a cap (a write in flight) is a plain disable.
 */
@Composable
internal fun LevelSelect(
    label: String,
    current: String,
    levels: List<String>,
    enabled: Boolean,
    cappedBy: String? = null,
    onChange: (String) -> Unit,
) {
    // The current level stays listed when the route does not offer it (a custom grant, or a
    // level above the caller's own), so the control never misstates what the grant holds.
    val options = levelOptionsFor(current, levels)
    Select(attrs = {
        classes("share-level")
        attr("aria-label", label)
        if (cappedBy != null) {
            classes("capped")
            attr("aria-disabled", "true")
            attr("aria-describedby", cappedBy)
        } else if (!enabled) {
            attr("disabled", "")
        }
        onChange { ev -> ev.value?.let { if (it != current) onChange(it) } }
    }) {
        options.forEach { l ->
            Option(l, attrs = {
                if (l == current) selected()
                if (l !in levels) attr("disabled", "")
            }) { Text(levelLabel(l)) }
        }
    }
}

@Composable
private fun AccessList(dialog: ShareDialogStore, access: FileAccessResponse) {
    Div(attrs = { classes("share-list-label") }) { Text("Who has access") }
    dialog.refreshError?.let { Div(attrs = { classes("share-note"); attr("role", "status") }) { Text(it) } }
    Div(attrs = { classes("share-access") }) {
        Div(attrs = { classes("share-row") }) {
            KindIcon(PickerKind.PERSON)
            Div(attrs = { classes("share-who") }) {
                Div(attrs = { classes("share-name") }) { Text(principalLabel("USER", access.owner, access.peopleStatus)) }
                access.owner.email?.let { Div(attrs = { classes("share-meta") }) { Text(it) } }
            }
            Span(attrs = { classes("share-role") }) { Text("Owner") }
        }
        access.grants.forEach { g ->
            key(g.grantId) {
                // Rebuilt when the level, the cap or a write's end changes, so a refused change
                // never leaves the select showing a level the grant does not hold.
                key(g.level, dialog.rowCaps[g.grantId], dialog.rowRevision[g.grantId]) {
                    GrantRow(dialog, g)
                }
            }
        }
    }
}

@Composable
private fun GrantRow(dialog: ShareDialogStore, g: AccessGrantDto) {
    val c = dialog.controlsFor(g)
    val busy = g.grantId in dialog.rowBusy
    val sentenceId = "share-row-cap-${g.grantId}"
    Div(attrs = { classes("share-row"); attr("data-grant", g.grantId) }) {
        KindIcon(if (g.principalKind == "TEAM") PickerKind.TEAM else PickerKind.PERSON)
        Div(attrs = { classes("share-who") }) {
            Div(attrs = { classes("share-name") }) { Text(c.name) }
            c.detail?.let { Div(attrs = { classes("share-meta") }) { Text(it) } }
        }
        LevelSelect(
            label = "Access for ${c.name}",
            current = g.level,
            levels = dialog.levels,
            enabled = !busy,
            cappedBy = if (!c.levelEnabled && c.sentence != null) sentenceId else null,
            onChange = { dialog.changeLevel(g, it) },
        )
        c.removeAction?.let { action ->
            Button(attrs = {
                classes("btn")
                attr("aria-label", if (action == RemoveAction.LEAVE) c.removeLabel!! else "Remove ${c.name}")
                if (busy) attr("disabled", "")
                onClick { if (!busy) dialog.remove(g) }
            }) { Text(c.removeLabel!!) }
        }
        // One sentence beside the capped control (S8), never an error.
        c.sentence?.let { Div(attrs = { classes("capped-notice"); attr("id", sentenceId); attr("role", "status") }) { Text(it) } }
    }
}
